import { useCallback, useEffect, useState } from 'react';

import {
  getAdmissionSummary, updateAdmissionSettings, applyStudioPreset,
  type AdmissionSummary, MODE_LABEL,
} from '../../lib/admissionsAdmin';
import type { AdmissionMode } from '../../lib/admissions';
import { ApiError } from '../../lib/apiClient';
import { useRouteBase } from '../routeBase';
import { AdmissionQueues } from './AdmissionQueues';
import './Admissions.css';

const SELECTABLE: AdmissionMode[] = ['ticketed', 'rsvp', 'request', 'invite_only'];

/**
 * Admissions, on the event that owns them.
 *
 * ── BUSINESS BEHAVIOUR LIVES HERE, NOT IN THE CMS EDITOR ─────────────────────────────
 *
 * §37/§87/§90. How an event admits people is an operational setting on the event. The CMS
 * decides where the admission surface sits on the page; it does not decide what that surface
 * does. Putting the mode in a template would mean a template copied between two events
 * carried the wrong admission behaviour with it.
 *
 * ── ONLY THE SETTINGS THE MODE ACTUALLY USES ─────────────────────────────────────────
 *
 * §89. A ticketed event shows nothing but a pointer to its tiers; an RSVP event shows
 * capacity, party range and waitlist. One long form of unrelated switches is how somebody
 * sets a party size on an event that has no parties.
 */
export function AdmissionsPanel({ eventId }: { eventId: string }) {
  const base = useRouteBase();
  const [summary, setSummary] = useState<AdmissionSummary | null>(null);
  const [error, setError] = useState<string | null>(null);
  const [flash, setFlash] = useState<string | null>(null);
  const [busy, setBusy] = useState(false);
  const [open, setOpen] = useState(false);

  const load = useCallback(async () => {
    try {
      setSummary(await getAdmissionSummary(eventId));
      setError(null);
    } catch (err) {
      setError(err instanceof ApiError ? err.message : 'Admissions could not be loaded.');
    }
  }, [eventId]);

  useEffect(() => { if (base === '/admin') void load(); }, [base, load]);

  // Admin only, same reasoning as the CMS panel: EventManage is shared with the Organizer
  // portal, and the admissions queues are an operator surface.
  if (base !== '/admin') return null;

  async function patch(body: Parameters<typeof updateAdmissionSettings>[1], note: string) {
    setBusy(true);
    setError(null);
    try {
      await updateAdmissionSettings(eventId, body);
      setFlash(note);
      await load();
    } catch (err) {
      setError(describe(err));
    } finally { setBusy(false); }
  }

  async function studioPreset() {
    if (!window.confirm(
      'Apply the Studio Audience preset? It sets this event to take requests, turns the '
      + 'waitlist on and allows parties of up to four. Every one of those is an ordinary '
      + 'setting afterwards and can be changed.',
    )) return;
    setBusy(true);
    try {
      await applyStudioPreset(eventId);
      setFlash('Studio Audience settings applied. Adjust anything below as needed.');
      await load();
    } catch (err) {
      setError(describe(err));
    } finally { setBusy(false); }
  }

  if (!summary) {
    return (
      <section className="org-card">
        <header className="org-card__head"><h2 className="org-card__title">Admissions</h2></header>
        {error
          ? <p className="alert alert--error" role="alert">{error}</p>
          : <p className="adm-muted">Loading admissions…</p>}
      </section>
    );
  }

  const s = summary.settings;
  const usesAdmissionCapacity = s.mode === 'rsvp' || s.mode === 'request' || s.mode === 'invite_only';

  return (
    <section className="org-card adm-admissions">
      <header className="org-card__head">
        <h2 className="org-card__title">Admissions</h2>
        <button type="button" className="button button--secondary"
          onClick={() => setOpen((v) => !v)}>
          {open ? 'Close' : 'Settings'}
        </button>
      </header>

      {flash && <p className="alert alert--success" role="status">{flash}</p>}
      {error && <p className="alert alert--error" role="alert">{error}</p>}

      <div className="adm-admissions__summary">
        <Fact label="How people get in" value={MODE_LABEL[s.mode]} />
        {s.preset && <Fact label="Preset" value="Studio Audience" />}
        {usesAdmissionCapacity && (
          <Fact label="Admission capacity"
            value={s.admissionCapacity == null ? 'Unlimited' : String(s.admissionCapacity)} />
        )}
        <Fact label="Admitted" value={String(summary.admitted)} />
        {usesAdmissionCapacity && summary.remaining != null && (
          <Fact label="Remaining" value={String(summary.remaining)} />
        )}
        {usesAdmissionCapacity && (
          <Fact label="Party size"
            value={s.partySizeMin === s.partySizeMax
              ? String(s.partySizeMax) : `${s.partySizeMin}–${s.partySizeMax}`} />
        )}
        {usesAdmissionCapacity && (
          <Fact label="Waitlist" value={s.waitlistEnabled ? 'On' : 'Off'} />
        )}
      </div>

      {/* §87: the room, as context. It is a safety ceiling, not the inventory. */}
      {summary.space && (
        <p className="adm-hint">
          {summary.space.name ?? 'This space'} holds{' '}
          {summary.space.capacity ?? 'an unrecorded number of'} people.
          {s.physicalCapacityOverrideAt && (
            <strong> Admission capacity was deliberately set above it.</strong>
          )}
        </p>
      )}

      {s.mode === 'ticketed' && (
        <p className="adm-hint">
          People get in by buying a ticket, and capacity comes from the ticket tiers below.
          Admission capacity, party size and the waitlist do not apply.
        </p>
      )}

      {open && (
        <AdmissionSettingsForm summary={summary} busy={busy}
          onPatch={patch} onStudioPreset={studioPreset} />
      )}

      {usesAdmissionCapacity && <AdmissionQueues eventId={eventId} settings={s} onChanged={load} />}
    </section>
  );
}

function Fact({ label, value }: { label: string; value: string }) {
  return (
    <div className="adm-admissions__fact">
      <span className="adm-admissions__fact-label">{label}</span>
      <span className="adm-admissions__fact-value">{value}</span>
    </div>
  );
}

function AdmissionSettingsForm({
  summary, busy, onPatch, onStudioPreset,
}: {
  summary: AdmissionSummary;
  busy: boolean;
  onPatch: (body: Parameters<typeof updateAdmissionSettings>[1], note: string) => Promise<void>;
  onStudioPreset: () => Promise<void>;
}) {
  const s = summary.settings;
  const [mode, setMode] = useState<AdmissionMode>(s.mode);
  const [capacity, setCapacity] = useState<string>(
    s.admissionCapacity == null ? '' : String(s.admissionCapacity));
  const [partyMin, setPartyMin] = useState(s.partySizeMin);
  const [partyMax, setPartyMax] = useState(s.partySizeMax);
  const [waitlist, setWaitlist] = useState(s.waitlistEnabled);
  const [authorize, setAuthorize] = useState(false);
  const [reason, setReason] = useState('');

  const usesCapacity = mode === 'rsvp' || mode === 'request' || mode === 'invite_only';
  // §38: once there is activity the server refuses most mode changes. Saying so here means
  // somebody is told before they try rather than after.
  const frozen = summary.activity.soldTickets > 0 || summary.activity.admissions > 0;
  const overRoom = summary.space?.capacity != null
    && capacity !== '' && Number(capacity) > summary.space.capacity;

  return (
    <form
      className="adm-admissions__form"
      onSubmit={(e) => {
        e.preventDefault();
        void onPatch({
          mode,
          waitlistEnabled: usesCapacity ? waitlist : false,
          partySizeMin: Number(partyMin),
          partySizeMax: Number(partyMax),
          admissionCapacity: capacity === '' ? null : Number(capacity),
          authorizeOverPhysicalCapacity: authorize,
          overrideReason: reason || null,
        }, 'Admission settings saved.');
      }}
    >
      <label className="field">
        <span className="field__label">How people get in</span>
        <select className="field__input" value={mode} disabled={busy}
          onChange={(e) => setMode(e.target.value as AdmissionMode)}>
          {SELECTABLE.map((m) => <option key={m} value={m}>{MODE_LABEL[m]}</option>)}
        </select>
        {frozen && (
          <span className="field__help">
            This event already has {summary.activity.soldTickets > 0
              ? `${summary.activity.soldTickets} ticket(s) sold`
              : `${summary.activity.admissions} confirmed admission(s)`}.
            Most changes are refused while that is true, because the existing records would
            describe a process the event no longer runs.
          </span>
        )}
        {/* §42: named and explained rather than silently missing. */}
        <span className="field__help">
          Donation admission is not available yet: it needs an amount, a payment and a
          donation receipt that is not a ticket receipt.
        </span>
      </label>

      {usesCapacity && (
        <>
          <label className="field">
            <span className="field__label">Admission capacity</span>
            <input className="field__input" type="number" min={0} value={capacity}
              placeholder="Unlimited" disabled={busy}
              onChange={(e) => setCapacity(e.target.value)} />
            <span className="field__help">
              People, not submissions: a party of four uses four places. Leave empty for no
              limit. Separate from ticket tier inventory.
            </span>
          </label>

          <div className="wiz-grid">
            <label className="field">
              <span className="field__label">Smallest party</span>
              <input className="field__input" type="number" min={1} value={partyMin}
                disabled={busy} onChange={(e) => setPartyMin(Number(e.target.value))} />
            </label>
            <label className="field">
              <span className="field__label">Largest party</span>
              <input className="field__input" type="number" min={1} value={partyMax}
                disabled={busy} onChange={(e) => setPartyMax(Number(e.target.value))} />
            </label>
          </div>

          <label className="field field--check">
            <input type="checkbox" checked={waitlist} disabled={busy}
              onChange={(e) => setWaitlist(e.target.checked)} />
            <span className="field__label">Offer a waitlist when full</span>
            <span className="field__help">
              People who cannot be admitted now can hold a place. Nobody is promoted
              automatically — an operator decides.
            </span>
          </label>

          {/* §15: shown only when it is actually in play. */}
          {overRoom && (
            <div className="alert alert--warning">
              <p>
                <strong>That is more than the room holds.</strong>{' '}
                {summary.space?.name ?? 'This space'} holds {summary.space?.capacity}. This is
                a safety limit, so exceeding it has to be authorised deliberately and the
                authorisation is recorded against your name.
              </p>
              <label className="field field--check">
                <input type="checkbox" checked={authorize}
                  onChange={(e) => setAuthorize(e.target.checked)} />
                <span className="field__label">I am authorising this deliberately</span>
              </label>
              <label className="field">
                <span className="field__label">Why</span>
                <input className="field__input" value={reason}
                  placeholder="e.g. standing room approved by the fire marshal"
                  onChange={(e) => setReason(e.target.value)} />
              </label>
            </div>
          )}
        </>
      )}

      <div className="adm-actions">
        <button type="submit" className="button button--primary" disabled={busy}>
          {busy ? 'Saving…' : 'Save admission settings'}
        </button>
        {!s.preset && (
          <button type="button" className="button button--ghost" disabled={busy}
            onClick={() => { void onStudioPreset(); }}>
            Apply Studio Audience preset
          </button>
        )}
      </div>
    </form>
  );
}

function describe(err: unknown): string {
  if (err instanceof ApiError) {
    const payload = err.payload as { message?: string; error?: string } | null;
    return payload?.message ?? payload?.error ?? err.message;
  }
  return 'That could not be saved.';
}
